// The HQ console's frame (spec/14 §1; #475). Every HQ page is inside it.
//
//  1. **Who is this?** `GET /v1/me/access` at platform scope: the platform role, its permissions and
//     the session's second factor (#475 rule 3).
//  2. **Not a platform role:** told plainly, and sent to the venue console (rule 2). No HQ navigation
//     is drawn for them.
//  3. **A platform role without a second factor:** sent to set one up, or to prove it — the console's
//     step-up screen, which every HQ route would ask for anyway (spec/07 §6.6, §7).
//  4. **Otherwise** the shell, with a sidebar of only the sections that are built and that the role
//     holds a permission of (rule 4, `sections.ts`).
//
// None of this guards anything: the server refuses a non-platform principal and a session without a
// second factor on every `/v1/hq/*` route (`apps/server/src/http/platform.ts`). The layout reads the
// role only so it can show the right thing.

import { useTranslation } from '@clavian/i18n/react';
import { Outlet, useRouterState } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';

import { usePlatformAccess } from '../api/queries';
import { useSecondFactorGate } from '../auth/second-factor-gate';
import { AppShell, useSignOut, type NavGroup, type PaletteAction } from '../shell/AppShell';
import { Loading } from '../shell/layouts';
import { LoadFailure } from '../shell/LoadFailure';
import { NavIcon } from '../shell/nav-icons';
import { PlatformAccessProvider } from './access';
import { NotForYourAccount } from './NotForYourAccount';
import type { SettingsTarget } from '../settings/search';
import { HQ_PATHS } from './paths';
import { HQ_GROUP_ORDER, HQ_SETTINGS, visibleSections } from './sections';

import './hq.css';

export function HqLayout(): ReactNode {
  const { t } = useTranslation('hq');
  const { t: tc } = useTranslation('console');
  const access = usePlatformAccess();
  const { require: requireSecondFactor } = useSecondFactorGate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const actions = useHqActions();

  const data = access.data;
  const needsSecondFactor = data !== undefined && data.role !== null && data.secondFactor.required && !data.secondFactor.satisfied;
  useEffect(() => {
    if (needsSecondFactor) requireSecondFactor();
  }, [needsSecondFactor, requireSecondFactor]);

  // What was read stays on screen if a refetch fails; only a first read that failed is an error.
  if (data === undefined) {
    return access.isError ? <LoadFailure error={access.error} onRetry={() => void access.refetch()} /> : <Loading />;
  }
  if (data.role === null) return <NotForYourAccount />;
  // The step-up screen covers this; once the session has the factor, the access is read again.
  if (needsSecondFactor) return <Loading />;

  // Only what is built and permitted (spec/14 §1), now grouped by job and given icons. HQ shows no
  // `Soon` entries — a section a role has no permission for must fail closed, not tease.
  const visible = visibleSections(data.permissions);
  const groups: NavGroup[] = HQ_GROUP_ORDER.map((group) => ({
    id: group,
    label: tc(`shell.group.${group}`),
    items: visible
      .filter((section) => section.group === group)
      .map((section) => ({
        id: section.id,
        label: t(`nav.${section.id}`),
        icon: <NavIcon name={section.id} />,
        to: section.to,
        params: {},
        ...(section.owns === undefined ? {} : { current: section.owns(pathname) }),
      })),
  })).filter((group) => group.items.length > 0);

  return (
    <PlatformAccessProvider access={data}>
      <AppShell
        area={
          <span className="console-area">
            <span className="console-area__badge">{t('shell.badge')}</span>
            <span>{t('shell.area')}</span>
          </span>
        }
        groups={groups}
        navLabel={t('shell.mainNav')}
        actions={actions}
        // HQ's palette finds the platform's settings — for a role that holds `hq:settings` (spec/14 §14).
        settingsSearch={data.permissions.has(HQ_SETTINGS) ? [platformTarget(tc('palette.settings.scope.platform'))] : []}
      >
        <Outlet />
      </AppShell>
    </PlatformAccessProvider>
  );
}

/** HQ's platform settings screen, as the palette's settings search opens it. */
function platformTarget(scopeLabel: string): SettingsTarget {
  return { id: 'platform', scope: { kind: 'platform' }, scopeLabel, to: HQ_PATHS.settingsGroup, params: {} };
}

/** The HQ palette's one action so far: sign out. */
function useHqActions(): PaletteAction[] {
  const { t } = useTranslation('console');
  const signOut = useSignOut();
  return [{ id: 'sign-out', label: t('shell.signOut'), icon: <NavIcon name="signout" />, run: () => void signOut() }];
}
